import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, numeric } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants, companies, areas, sites, projects, regions } from './common';

export const equipment = pgTable("equipment", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name"),
  code: text("code"),
  category: text("category"),
  assetCode: text("asset_code"),
  serialNumber: text("serial_number"),
  tagNumber: text("tag_number"),
  barcodeRef: text("barcode_ref"),
  displayName: text("display_name"),
  description: text("description"),
  assetCategory: text("asset_category"),
  assetSubtype: text("asset_subtype"),
  safetyCriticalFlag: boolean("safety_critical_flag").notNull().default(false),
  certificationRequiredFlag: boolean("certification_required_flag").notNull().default(false),
  inspectionRequiredFlag: boolean("inspection_required_flag").notNull().default(false),
  maintenanceRequiredFlag: boolean("maintenance_required_flag").notNull().default(false),
  operatorCompetencyRequiredFlag: boolean("operator_competency_required_flag").notNull().default(false),
  ptwLinkageFlag: boolean("ptw_linkage_flag").notNull().default(false),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  regionId: text("region_id").references(() => regions.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  ownerCompanyId: text("owner_company_id").references(() => companies.id),
  hostCompanyId: text("host_company_id").references(() => companies.id),
  currentCustodianId: text("current_custodian_id"),
  status: text("status").notNull().default("available"),
  manufacturer: text("manufacturer"),
  model: text("model"),
  make: text("make"),
  year: text("year"),
  capacity: text("capacity"),
  specificationSummary: jsonb("specification_summary").$type<Record<string, any>>(),
  acquisitionDate: text("acquisition_date"),
  commissioningDate: text("commissioning_date"),
  serviceStartDate: text("service_start_date"),
  expectedEndOfLife: text("expected_end_of_life"),
  lifecycleState: text("lifecycle_state").notNull().default("draft"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  isDraft: boolean("is_draft").notNull().default(false),
  draftSource: text("draft_source"),
});
export const insertEquipmentSchema = createInsertSchema(equipment).omit({ id: true, tenantId: true });
export type InsertEquipment = z.infer<typeof insertEquipmentSchema>;
export type Equipment = typeof equipment.$inferSelect;
export const tools = pgTable("tools", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  toolCode: text("tool_code"),
  serialNumber: text("serial_number"),
  toolCategory: text("tool_category").notNull(),
  toolType: text("tool_type"),
  description: text("description"),
  ownershipCompanyId: text("ownership_company_id"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  currentAssigneeId: text("current_assignee_id"),
  status: text("status").notNull().default("available"),
  inspectionRequiredFlag: boolean("inspection_required_flag").notNull().default(false),
  certificationRequiredFlag: boolean("certification_required_flag").notNull().default(false),
  calibrationRequiredFlag: boolean("calibration_required_flag").notNull().default(false),
  maintenanceRequiredFlag: boolean("maintenance_required_flag").notNull().default(false),
  consumableVsReusable: text("consumable_vs_reusable").notNull().default("reusable"),
  lifecycleState: text("lifecycle_state").notNull().default("draft"),
  lastInspectionDate: text("last_inspection_date"),
  nextInspectionDue: text("next_inspection_due"),
  lastCertificationDate: text("last_certification_date"),
  nextCertificationDue: text("next_certification_due"),
  lastCalibrationDate: text("last_calibration_date"),
  nextCalibrationDue: text("next_calibration_due"),
  lastMaintenanceDate: text("last_maintenance_date"),
  nextMaintenanceDue: text("next_maintenance_due"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertToolSchema = createInsertSchema(tools).omit({ id: true, tenantId: true });
export type InsertTool = z.infer<typeof insertToolSchema>;
export type Tool = typeof tools.$inferSelect;
export const equipmentClassifications = pgTable("equipment_classifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  assetClass: text("asset_class").notNull(),
  equipmentFamily: text("equipment_family"),
  operationalUse: text("operational_use"),
  safetyCriticality: text("safety_criticality").notNull().default("standard"),
  inspectionRegime: text("inspection_regime"),
  maintenanceRegime: text("maintenance_regime"),
  certificationRegime: text("certification_regime"),
  areaCompatibility: text("area_compatibility").array().default([]),
  operatorRequirement: text("operator_requirement"),
  ownershipModel: text("ownership_model"),
  description: text("description"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertEquipmentClassificationSchema = createInsertSchema(equipmentClassifications).omit({ id: true, tenantId: true });
export type InsertEquipmentClassification = z.infer<typeof insertEquipmentClassificationSchema>;
export type EquipmentClassification = typeof equipmentClassifications.$inferSelect;
export const equipmentAssignments = pgTable("equipment_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  equipmentId: text("equipment_id"),
  toolId: text("tool_id"),
  assignmentType: text("assignment_type").notNull(),
  ownerCompanyId: text("owner_company_id"),
  assignedCompanyId: text("assigned_company_id"),
  workerId: text("worker_id"),
  crewId: text("crew_id"),
  supervisorId: text("supervisor_id"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  ptwId: text("ptw_id"),
  workPackageId: text("work_package_id"),
  storageLocation: text("storage_location"),
  startDatetime: text("start_datetime"),
  endDatetime: text("end_datetime"),
  status: text("status").notNull().default("planned"),
  issueCondition: text("issue_condition"),
  returnCondition: text("return_condition"),
  approvedBy: text("approved_by"),
  transferredFromId: text("transferred_from_id"),
  notes: text("notes"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertEquipmentAssignmentSchema = createInsertSchema(equipmentAssignments).omit({ id: true, tenantId: true });
export type InsertEquipmentAssignment = z.infer<typeof insertEquipmentAssignmentSchema>;
export type EquipmentAssignment = typeof equipmentAssignments.$inferSelect;

// ─── Inspection Templates ────────────────────────────────────────────
export const inspectionTemplates = pgTable("inspection_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  inspectionCode: text("inspection_code"),
  name: text("name").notNull(),
  code: text("code"),
  description: text("description"),
  inspectionType: text("inspection_type").notNull().default("periodic"),
  applicableCategories: text("applicable_categories").array().default([]),
  applicableEquipmentCategories: text("applicable_equipment_categories").array().default([]),
  frequency: text("frequency"),
  checklist: jsonb("checklist").$type<{
    id: string;
    title: string;
    type: string;
    mandatory: boolean;
    description?: string;
    acceptanceCriteria?: string;
  }[]>().default([]),
  checklistFields: jsonb("checklist_fields").$type<{
    fieldId: string;
    label: string;
    fieldType: string;
    required: boolean;
    options?: string[];
  }[]>().default([]),
  measuredValueFields: jsonb("measured_value_fields").$type<{
    key: string;
    label: string;
    unit: string;
    minValue?: number;
    maxValue?: number;
  }[]>().default([]),
  passFailLogic: jsonb("pass_fail_logic").$type<Record<string, any>>(),
  requiredEvidenceTypes: text("required_evidence_types").array().default([]),
  evidenceRequirements: text("evidence_requirements").array().default([]),
  inspectorRoleRequirements: text("inspector_role_requirements").array().default([]),
  defaultIntervalDays: integer("default_interval_days"),
  regulatoryReference: text("regulatory_reference"),
  riskLevel: text("risk_level").default("medium"),
  severityOnFailure: text("severity_on_failure").notNull().default("medium"),
  reinspectionRules: jsonb("reinspection_rules").$type<Record<string, any>>(),
  requiresPostMaintenanceInspection: boolean("requires_post_maintenance_inspection").notNull().default(false),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("active"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  companyScope: text("company_scope"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertInspectionTemplateSchema = createInsertSchema(inspectionTemplates).omit({ id: true, tenantId: true });
export type InsertInspectionTemplate = z.infer<typeof insertInspectionTemplateSchema>;
export type InspectionTemplate = typeof inspectionTemplates.$inferSelect;

// ─── Inspection Records ──────────────────────────────────────────────
export const inspectionRecords = pgTable("inspection_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  inspectionTemplateId: text("inspection_template_id").references(() => inspectionTemplates.id),
  templateId: text("template_id"),
  equipmentId: text("equipment_id").references(() => equipment.id),
  equipmentCategory: text("equipment_category"),
  toolId: text("tool_id"),
  inspectionType: text("inspection_type").notNull().default("periodic"),
  triggerType: text("trigger_type").notNull().default("scheduled"),
  status: text("status").notNull().default("scheduled"),
  scheduledDate: text("scheduled_date"),
  dueDate: text("due_date"),
  performedDate: text("performed_date"),
  nextDueDate: text("next_due_date"),
  inspectorId: text("inspector_id"),
  inspectorName: text("inspector_name"),
  inspectorRole: text("inspector_role"),
  checklistResults: jsonb("checklist_results").$type<{
    itemId: string;
    title: string;
    result: string;
    notes?: string;
  }[]>().default([]),
  measuredValues: jsonb("measured_values").$type<{
    key: string;
    label: string;
    value: number;
    unit: string;
    inRange: boolean;
  }[]>().default([]),
  overallResult: text("overall_result"),
  outcome: text("outcome").notNull().default("pending"),
  evidence: text("evidence").array().default([]),
  evidenceReferences: text("evidence_references").array().default([]),
  defectFoundFlag: boolean("defect_found_flag").notNull().default(false),
  defectFlags: jsonb("defect_flags").$type<{
    id: string;
    description: string;
    severity: string;
    resolved: boolean;
  }[]>().default([]),
  verificationRequired: boolean("verification_required").notNull().default(false),
  verifiedBy: text("verified_by"),
  verifiedDate: text("verified_date"),
  notes: text("notes"),
  linkedMaintenanceRequestId: text("linked_maintenance_request_id"),
  linkedMaintenanceId: text("linked_maintenance_id"),
  linkedHoldId: text("linked_hold_id"),
  postMaintenanceOf: text("post_maintenance_of"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  companyId: text("company_id"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertInspectionRecordSchema = createInsertSchema(inspectionRecords).omit({ id: true, tenantId: true });
export type InsertInspectionRecord = z.infer<typeof insertInspectionRecordSchema>;
export type InspectionRecord = typeof inspectionRecords.$inferSelect;

// ─── Maintenance Plans ───────────────────────────────────────────────
export const maintenancePlans = pgTable("maintenance_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  code: text("code"),
  description: text("description"),
  maintenanceType: text("maintenance_type").notNull().default("preventive"),
  assetCategoryApplicability: text("asset_category_applicability").array().default([]),
  applicableEquipmentCategories: text("applicable_equipment_categories").array().default([]),
  intervalModel: text("interval_model").notNull().default("calendar"),
  intervalValue: integer("interval_value"),
  intervalUnit: text("interval_unit").default("days"),
  frequency: text("frequency"),
  taskList: jsonb("task_list").$type<{
    id: string;
    title: string;
    description?: string;
    mandatory: boolean;
    estimatedMinutes?: number;
  }[]>().default([]),
  requiredTasks: jsonb("required_tasks").$type<{
    taskId: string;
    description: string;
    mandatory: boolean;
  }[]>().default([]),
  requiredParts: jsonb("required_parts").$type<{
    partNumber: string;
    description: string;
    quantity: number;
  }[]>().default([]),
  requiredSkills: text("required_skills").array().default([]),
  requiredTechnicianRole: text("required_technician_role"),
  estimatedDurationHours: numeric("estimated_duration_hours"),
  outOfServiceRequirement: boolean("out_of_service_requirement").notNull().default(false),
  requiresOutOfService: boolean("requires_out_of_service").notNull().default(false),
  postMaintenanceInspectionRequired: boolean("post_maintenance_inspection_required").notNull().default(false),
  requiresPostMaintenanceInspection: boolean("requires_post_maintenance_inspection").notNull().default(false),
  postMaintenanceInspectionTemplateId: text("post_maintenance_inspection_template_id"),
  regulatoryReference: text("regulatory_reference"),
  riskLevel: text("risk_level").default("medium"),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("active"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  companyScope: text("company_scope"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMaintenancePlanSchema = createInsertSchema(maintenancePlans).omit({ id: true, tenantId: true });
export type InsertMaintenancePlan = z.infer<typeof insertMaintenancePlanSchema>;
export type MaintenancePlan = typeof maintenancePlans.$inferSelect;

// ─── Maintenance Records ─────────────────────────────────────────────
export const maintenanceRecords = pgTable("maintenance_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  maintenancePlanId: text("maintenance_plan_id"),
  planId: text("plan_id"),
  equipmentId: text("equipment_id"),
  equipmentCategory: text("equipment_category"),
  toolId: text("tool_id"),
  maintenanceType: text("maintenance_type").notNull().default("preventive"),
  triggerType: text("trigger_type").notNull().default("scheduled"),
  status: text("status").notNull().default("scheduled"),
  scheduledDate: text("scheduled_date"),
  dueDate: text("due_date"),
  startedDate: text("started_date"),
  completedDate: text("completed_date"),
  nextDueDate: text("next_due_date"),
  maintainerId: text("maintainer_id"),
  maintainerName: text("maintainer_name"),
  maintainerRole: text("maintainer_role"),
  workPerformed: jsonb("work_performed").$type<{
    taskId: string;
    title: string;
    completed: boolean;
    notes?: string;
  }[]>().default([]),
  replacedParts: jsonb("replaced_parts").$type<{
    partId: string;
    partName: string;
    quantity: number;
  }[]>().default([]),
  partsReplaced: jsonb("parts_replaced").$type<{
    partNumber: string;
    description: string;
    quantity: number;
    serialNumber?: string;
  }[]>().default([]),
  findings: text("findings"),
  defectsFound: text("defects_found"),
  recommendations: text("recommendations"),
  evidence: text("evidence").array().default([]),
  evidenceReferences: text("evidence_references").array().default([]),
  outOfServiceFlag: boolean("out_of_service_flag").notNull().default(false),
  outOfService: boolean("out_of_service").notNull().default(false),
  outOfServiceDate: text("out_of_service_date"),
  backInServiceDate: text("back_in_service_date"),
  returnToServiceApproval: text("return_to_service_approval"),
  returnToServiceApproved: boolean("return_to_service_approved").notNull().default(false),
  returnToServiceApprovedBy: text("return_to_service_approved_by"),
  returnToServiceDate: text("return_to_service_date"),
  linkedInspectionRequestId: text("linked_inspection_request_id"),
  triggeredByInspectionId: text("triggered_by_inspection_id"),
  postMaintenanceInspectionId: text("post_maintenance_inspection_id"),
  certificationImpactFlag: boolean("certification_impact_flag").notNull().default(false),
  notes: text("notes"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  companyId: text("company_id"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMaintenanceRecordSchema = createInsertSchema(maintenanceRecords).omit({ id: true, tenantId: true });
export type InsertMaintenanceRecord = z.infer<typeof insertMaintenanceRecordSchema>;
export type MaintenanceRecord = typeof maintenanceRecords.$inferSelect;
export const equipmentCertifications = pgTable("equipment_certifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  equipmentId: text("equipment_id"),
  toolId: text("tool_id"),
  certificationType: text("certification_type").notNull(),
  certificateNumber: text("certificate_number"),
  issuingAuthority: text("issuing_authority"),
  issuerTrustStatus: text("issuer_trust_status").notNull().default("trusted"),
  certificateStatus: text("certificate_status").notNull().default("active"),
  issueDate: text("issue_date"),
  effectiveDate: text("effective_date"),
  expiryDate: text("expiry_date"),
  gracePeriodDays: integer("grace_period_days").default(0),
  nextRenewalDue: text("next_renewal_due"),
  scopeRestrictions: jsonb("scope_restrictions").$type<Record<string, any>>(),
  certificateDocument: text("certificate_document"),
  supportingAttachments: text("supporting_attachments").array().default([]),
  inspectionLinkageId: text("inspection_linkage_id"),
  calibrationReportLinkageId: text("calibration_report_linkage_id"),
  verifiedFlag: boolean("verified_flag").notNull().default(false),
  verifiedBy: text("verified_by"),
  verifiedDate: text("verified_date"),
  approvedFlag: boolean("approved_flag").notNull().default(false),
  approvedBy: text("approved_by"),
  approvedDate: text("approved_date"),
  suspendedFlag: boolean("suspended_flag").notNull().default(false),
  suspendedDate: text("suspended_date"),
  suspendedReason: text("suspended_reason"),
  revokedFlag: boolean("revoked_flag").notNull().default(false),
  revokedDate: text("revoked_date"),
  revokedReason: text("revoked_reason"),
  linkedRestrictionId: text("linked_restriction_id"),
  notes: text("notes"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertEquipmentCertificationSchema = createInsertSchema(equipmentCertifications).omit({ id: true, tenantId: true });
export type InsertEquipmentCertification = z.infer<typeof insertEquipmentCertificationSchema>;
export type EquipmentCertification = typeof equipmentCertifications.$inferSelect;
export const equipmentDefects = pgTable("equipment_defects", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  equipmentId: text("equipment_id"),
  toolId: text("tool_id"),
  defectType: text("defect_type").notNull(),
  description: text("description").notNull(),
  detectedBy: text("detected_by"),
  detectedDatetime: text("detected_datetime"),
  severity: text("severity").notNull().default("medium"),
  immediateAction: text("immediate_action"),
  activeRestrictionState: text("active_restriction_state").notNull().default("none"),
  linkedInspectionId: text("linked_inspection_id"),
  linkedIncidentId: text("linked_incident_id"),
  linkedMaintenanceRequestId: text("linked_maintenance_request_id"),
  verificationNeededBeforeRelease: boolean("verification_needed_before_release").notNull().default(true),
  releasedBy: text("released_by"),
  releaseDatetime: text("release_datetime"),
  status: text("status").notNull().default("open"),
  notes: text("notes"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertEquipmentDefectSchema = createInsertSchema(equipmentDefects).omit({ id: true, tenantId: true });
export type InsertEquipmentDefect = z.infer<typeof insertEquipmentDefectSchema>;
export type EquipmentDefect = typeof equipmentDefects.$inferSelect;
export const equipmentAuditLog = pgTable("equipment_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  equipmentId: text("equipment_id"),
  toolId: text("tool_id"),
  actor: text("actor"),
  actorRole: text("actor_role"),
  oldState: jsonb("old_state").$type<Record<string, any>>(),
  newState: jsonb("new_state").$type<Record<string, any>>(),
  reason: text("reason"),
  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  linkedEvidence: text("linked_evidence").array().default([]),
  eventTimestamp: text("event_timestamp").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertEquipmentAuditLogSchema = createInsertSchema(equipmentAuditLog).omit({ id: true, tenantId: true });
export type InsertEquipmentAuditLog = z.infer<typeof insertEquipmentAuditLogSchema>;
export type EquipmentAuditLogEntry = typeof equipmentAuditLog.$inferSelect;

// ─── Emergency Muster Module ──────────────────────────────────────
export const inspectionMaintenanceAuditLog = pgTable("inspection_maintenance_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  eventTimestamp: text("event_timestamp").notNull(),
  previousValues: jsonb("previous_values").$type<Record<string, any>>(),
  newValues: jsonb("new_values").$type<Record<string, any>>(),
  changedFields: text("changed_fields").array().default([]),
  reason: text("reason"),
  notes: text("notes"),
  linkedEntityType: text("linked_entity_type"),
  linkedEntityId: text("linked_entity_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertInspectionMaintenanceAuditLogSchema = createInsertSchema(inspectionMaintenanceAuditLog).omit({ id: true, tenantId: true });
export type InsertInspectionMaintenanceAuditLog = z.infer<typeof insertInspectionMaintenanceAuditLogSchema>;
export type InspectionMaintenanceAuditLogEntry = typeof inspectionMaintenanceAuditLog.$inferSelect;

// ─── Documents, Records & Evidence Module ─────────────────────────────

// ─── Retention Policies ──────────────────────────────────────────────
